import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";

import { GetCountry } from "api/rpc/2024-04/country";
import { IAPIResponse, StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { ICountry } from "redux/reducers/models/customer";
import { TProductVendor, TVendorContact } from "redux/reducers/models/product";
import { useAppDispatch } from "hooks/redux";
import Page from "components/page/Page";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import { Select } from "components/select/";
import TextField from "components/form/textField/TextField";
import Spin from "components/spin/spin";

import {
  VendorFormState,
  VendorWrite,
  blankContact,
  blankVendorForm,
  createdVendorId,
  formFromVendor,
  isValidEmail,
  snapshotVendorForm,
  vendorPayload,
  withCountrySelection,
} from "./vendorFormState";
import "./vendors.scss";

interface IVendorFormProps {
  mode: "new" | "edit";
  vendorId?: string;
  listPath: string;
  editPath: (id: number) => string;
  load?: (id: number) => Promise<IAPIResponse<TProductVendor[]>>;
  save: (payload: VendorWrite & { id?: number }) => Promise<IAPIResponse<unknown>>;
}

export default function VendorForm(props: IVendorFormProps) {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { Option } = Select;

  const [countries, setCountries] = useState<ICountry[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [form, setForm] = useState<VendorFormState | null>(null);
  const [baseline, setBaseline] = useState("");
  const [nameError, setNameError] = useState("");
  const [emailErrors, setEmailErrors] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);

  useEffect(() => {
    const load = props.load;
    const vendorId = props.vendorId;
    let cancelled = false;

    async function loadForm() {
      const countryRes = await GetCountry({ provinces: true }, false);
      if (cancelled) {
        return;
      }
      const countryList = countryRes.status === StatusCode.OK ? countryRes.data ?? [] : [];
      if (countryRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error loading countries")));
      }
      setCountries(countryList);
      setCountriesLoading(false);

      if (props.mode === "new") {
        const canada = countryList.find(country => country.code === "CA");
        const next = canada
          ? { ...blankVendorForm(), countryId: canada.id, countryText: canada.name }
          : blankVendorForm();
        setForm(next);
        setBaseline(snapshotVendorForm(next));
        return;
      }

      if (!load || !vendorId) {
        return;
      }
      const res = await load(Number(vendorId));
      if (cancelled) {
        return;
      }
      if (res.status !== StatusCode.OK || !res.data?.[0]) {
        dispatch(showError(t("Error loading vendor")));
        history.push(props.listPath);
        return;
      }
      const next = withCountrySelection(formFromVendor(res.data[0]), countryList);
      setForm(next);
      setBaseline(snapshotVendorForm(next));
    }

    void loadForm();
    return () => {
      cancelled = true;
    };
  }, [props.mode, props.vendorId]);

  useEffect(() => {
    if (focusIndex === null) {
      return;
    }
    document.getElementById(`vendor-contact-${focusIndex}-name`)?.focus();
    setFocusIndex(null);
  }, [focusIndex, form?.contacts.length]);

  const dirty = form ? snapshotVendorForm(form) !== baseline : false;

  function updateField<K extends keyof VendorFormState>(key: K, value: VendorFormState[K]) {
    setForm(current => (current ? { ...current, [key]: value } : current));
    if (key === "title") {
      setNameError("");
    }
  }

  function updateContact(index: number, key: keyof TVendorContact, value: string) {
    setForm(current => {
      if (!current) {
        return current;
      }
      return {
        ...current,
        contacts: current.contacts.map((contact, contactIndex) =>
          contactIndex === index ? { ...contact, [key]: value } : contact,
        ),
      };
    });
    if (key === "email") {
      setEmailErrors(current => ({ ...current, [index]: "" }));
    }
  }

  function addContact() {
    if (!form) {
      return;
    }
    setFocusIndex(form.contacts.length);
    setForm({ ...form, contacts: [...form.contacts, blankContact()] });
  }

  function removeContact(index: number) {
    setForm(current => {
      if (!current || current.contacts.length < 2) {
        return current;
      }
      return { ...current, contacts: current.contacts.filter((_, contactIndex) => contactIndex !== index) };
    });
    setEmailErrors({});
  }

  function discard() {
    if (!baseline) {
      return;
    }
    setForm(JSON.parse(baseline) as VendorFormState);
    setNameError("");
    setEmailErrors({});
  }

  async function saveVendor() {
    if (!form || saving) {
      return;
    }

    let focusId: string | null = null;
    if (!form.title.trim()) {
      setNameError(t("Enter a vendor name."));
      focusId = "vendor-name";
    } else {
      setNameError("");
    }

    const nextEmailErrors: Record<number, string> = {};
    form.contacts.forEach((contact, index) => {
      if (contact.email.trim() && !isValidEmail(contact.email)) {
        nextEmailErrors[index] = t("Enter a valid email, like name@company.com.");
        focusId = focusId ?? `vendor-contact-${index}-email`;
      }
    });
    setEmailErrors(nextEmailErrors);

    if (focusId) {
      const field = document.getElementById(focusId);
      field?.scrollIntoView({ block: "center" });
      field?.focus();
      return;
    }

    setSaving(true);
    const payload = vendorPayload(form);
    const res = await props.save(props.mode === "edit" ? { ...payload, id: Number(props.vendorId) } : payload);
    setSaving(false);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.message === "string" && res.message ? res.message : t("Error saving vendor")));
      return;
    }

    dispatch(showSuccess(t("Vendor saved")));
    if (props.mode === "new") {
      const id = createdVendorId(res.data);
      history.replace(id ? props.editPath(id) : props.listPath);
      return;
    }
    setBaseline(snapshotVendorForm(form));
  }

  const selectedCountry = countries.find(country => country.id === form?.countryId);
  const provinces = selectedCountry?.provinces ?? [];
  const countryCode = selectedCountry?.code?.toUpperCase();
  const regionLabel = countryCode === "US" ? t("State") : countryCode && countryCode !== "CA" ? t("State / region") : t("Province");
  const postalLabel = countryCode === "US" ? t("ZIP code") : t("Postal code");

  function handleCountryChange(countryId: number) {
    const country = countries.find(item => item.id === countryId);
    if (!country) {
      return;
    }
    setForm(current => {
      // Select calls onChange when it mounts with a default value. Keep the
      // loaded province unless the user actually picks a different country.
      if (!current || current.countryId === country.id) {
        return current;
      }
      return {
        ...current,
        countryId: country.id,
        countryText: country.name,
        provinceId: null,
        provinceText: "",
      };
    });
  }

  function handleProvinceChange(provinceId: number) {
    const province = provinces.find(item => item.id === provinceId);
    if (!province) {
      return;
    }
    setForm(current => {
      if (!current || (current.provinceId === province.id && current.provinceText === province.name)) {
        return current;
      }
      return {
        ...current,
        provinceId: province.id,
        provinceText: province.name,
      };
    });
  }

  return (
    <Page
      className="vendor-screen"
      title={props.mode === "new" ? t("New Vendor") : t("Edit Vendor")}
      narrow
      breadcrumbs={[
        { label: t("Settings") },
        { label: t("Vendors"), url: props.listPath },
        props.mode === "new"
          ? { label: t("New Vendor"), url: `${props.listPath}/new` }
          : {
              label: form?.title.trim() || t("Edit Vendor"),
              url: props.vendorId ? props.editPath(Number(props.vendorId)) : props.listPath,
            },
      ]}
      notificationBarProps={{
        isVisible: dirty,
        onAction: () => void saveVendor(),
        onCancel: discard,
      }}
      primaryAction={{
        content: t("Save"),
        action: () => void saveVendor(),
        disabled: !dirty || saving || !form,
      }}
    >
      {!form ? (
        <div className="vendor-loading">
          <Spin />
        </div>
      ) : (
        <>
          <Card
            title={t("Vendor details")}
            subtitle={t("The name shown on purchase orders and in product records.")}
          >
            <Card.Section>
              <div className="vendor-grid">
                <div className="vendor-span-6">
                  <Input
                    id="vendor-name"
                    value={form.title}
                    label={t("Vendor name")}
                    onChange={event => updateField("title", event.target.value)}
                    autoComplete="organization"
                    maxLength={100}
                    error={!!nameError}
                    helpText={nameError || undefined}
                  />
                </div>
              </div>
            </Card.Section>
          </Card>

          <Card title={t("Address")} subtitle={t("Where orders are shipped from or invoices are sent.")}>
            <Card.Section>
              <div className="vendor-grid">
                <div className="vendor-span-6">
                  <Input
                    id="vendor-address-1"
                    value={form.addressLine1}
                    label={t("Address line 1")}
                    placeholder={t("Street address")}
                    autoComplete="address-line1"
                    onChange={event => updateField("addressLine1", event.target.value)}
                  />
                </div>
                <div className="vendor-span-6">
                  <Input
                    id="vendor-address-2"
                    value={form.addressLine2}
                    label={
                      <>
                        {t("Address line 2")} <span className="vendor-optional">{t("(optional)")}</span>
                      </>
                    }
                    placeholder={t("Suite, unit, building")}
                    autoComplete="address-line2"
                    onChange={event => updateField("addressLine2", event.target.value)}
                  />
                </div>
                <div className="vendor-span-2">
                  <Input
                    id="vendor-city"
                    value={form.city}
                    label={t("City")}
                    autoComplete="address-level2"
                    onChange={event => updateField("city", event.target.value)}
                  />
                </div>
                <div className="vendor-span-2">
                  <Select
                    key={`vendor-province-${form.countryId ?? "none"}-${form.provinceId ?? "none"}`}
                    id="vendor-province"
                    label={regionLabel}
                    placeholder={form.provinceId ? undefined : form.provinceText || t("Select a province")}
                    showPlaceholder={!form.provinceId}
                    defaultValue={form.provinceId ?? undefined}
                    loading={countriesLoading}
                    disabled={!form.countryId}
                    onChange={handleProvinceChange}
                  >
                    {provinces.map(province => (
                      <Option key={province.id} value={province.id}>
                        {province.name}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div className="vendor-span-2">
                  <Input
                    id="vendor-postal"
                    value={form.postalCode}
                    label={postalLabel}
                    autoComplete="postal-code"
                    onChange={event => updateField("postalCode", event.target.value)}
                  />
                </div>
                <div className="vendor-span-3">
                  <Select
                    key={`vendor-country-${form.countryId ?? "none"}`}
                    id="vendor-country"
                    label={t("Country")}
                    placeholder={form.countryId ? undefined : form.countryText || t("Select a country")}
                    showPlaceholder={!form.countryId}
                    defaultValue={form.countryId ?? undefined}
                    loading={countriesLoading}
                    onChange={handleCountryChange}
                  >
                    {countries.map(country => (
                      <Option key={country.id} value={country.id}>
                        {country.name}
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
            </Card.Section>
          </Card>

          <Card title={t("Sales rep")} subtitle={t("Who to contact when you place or follow up on an order.")}>
            <Card.Section>
              {form.contacts.map((contact, index) => (
                <div className="vendor-contact" key={index}>
                  <div className="vendor-contact-head">
                    <h3>
                      {index === 0 ? t("Primary contact") : t("Contact {{number}}", { number: index + 1 })}
                      {index === 0 ? <span className="vendor-badge">{t("Primary")}</span> : null}
                    </h3>
                    {form.contacts.length > 1 ? (
                      <button
                        className="vendor-remove"
                        type="button"
                        aria-label={t("Remove contact {{number}}", { number: index + 1 })}
                        onClick={() => removeContact(index)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        {t("Remove")}
                      </button>
                    ) : null}
                  </div>
                  <div className="vendor-grid">
                    <div className="vendor-span-3">
                      <Input
                        id={`vendor-contact-${index}-name`}
                        value={contact.name}
                        label={t("Contact name")}
                        autoComplete="off"
                        onChange={event => updateContact(index, "name", event.target.value)}
                      />
                    </div>
                    <div className="vendor-span-3">
                      <Input
                        id={`vendor-contact-${index}-role`}
                        value={contact.role}
                        label={
                          <>
                            {t("Job title")} <span className="vendor-optional">{t("(optional)")}</span>
                          </>
                        }
                        placeholder={t("e.g. Territory sales rep")}
                        autoComplete="off"
                        onChange={event => updateContact(index, "role", event.target.value)}
                      />
                    </div>
                    <div className="vendor-span-3">
                      <Input
                        id={`vendor-contact-${index}-email`}
                        value={contact.email}
                        label={t("Email")}
                        type="email"
                        autoComplete="off"
                        error={!!emailErrors[index]}
                        helpText={emailErrors[index] || undefined}
                        onChange={event => updateContact(index, "email", event.target.value)}
                      />
                    </div>
                    <div className="vendor-span-3">
                      <Input
                        id={`vendor-contact-${index}-phone`}
                        value={contact.phone}
                        label={t("Phone")}
                        type="tel"
                        autoComplete="off"
                        onChange={event => updateContact(index, "phone", event.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button className="vendor-add" type="button" onClick={addContact}>
                <FontAwesomeIcon icon={faPlus} />
                {t("Add another contact")}
              </button>
            </Card.Section>
          </Card>

          <Card
            title={t("Notes")}
            subtitle={t("Account numbers, ordering cutoffs, return terms, anything your team should know.")}
          >
            <Card.Section>
              <TextField
                id="vendor-notes"
                label={t("Notes")}
                labelHidden
                value={form.notes}
                maxLength={1000}
                rows={4}
                onChange={event => updateField("notes", event.target.value)}
              />
            </Card.Section>
          </Card>
        </>
      )}
    </Page>
  );
}
